// src/pages/ForgotPassword.tsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GlobalStyles } from "../styles/globalStyles";
import { Mail, ArrowLeft, Shield, ArrowRight } from "lucide-react";
import logo from "../assets/images/logo.png";
import toast from "react-hot-toast";
import { authService } from "../services/api";

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    try {
      const response = await authService.forgotPassword(email);

      if (response.success) {
        toast.success("Password reset OTP sent to your email!");
        navigate("/reset-password", { state: { email } });
      } else {
        toast.error(response.message || "Failed to send OTP");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <div className="mb-6">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to login
          </Link>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <img src={logo} alt="Pelivor Recovery" className="h-12 w-auto" />
              <div className="absolute -top-1 -right-1 h-3 w-3 bg-emerald-400 rounded-full animate-pulse border-2 border-white dark:border-gray-900"></div>
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Reset Password
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Enter your email to receive a verification code
            </p>
          </div>
        </div>

        {/* Forgot Password Card */}
        <div className={`${GlobalStyles.card} !p-8`}>
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Mail className="h-8 w-8 text-primary" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              We'll send a 6-digit verification code to your email address
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div className="space-y-2">
              <label
                htmlFor="email"
                className={`${GlobalStyles.inputLabel} flex items-center gap-2`}
              >
                <Mail className="h-4 w-4 text-primary" />
                <span>Email Address</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={GlobalStyles.input}
                placeholder="Enter your email address"
                disabled={isLoading}
              />
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} w-full group/submit`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div
                      className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}
                    ></div>
                    Sending code...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <span>Send Verification Code</span>
                    <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover/submit:translate-x-1" />
                  </div>
                )}
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400">
                Remember your password?
              </span>
            </div>
          </div>

          {/* Login Link */}
          <Link
            to="/login"
            className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full`}
          >
            Back to Login
          </Link>

          {/* Security Info */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Shield className="h-4 w-4 text-emerald-500" />
              <span>Secure OTP delivery • Expires in 10 minutes</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
